import { Button, HStack, Spacer, Stack, Text, useDisclosure } from '@chakra-ui/react';
import { useCallback, useMemo } from 'react';
import { keyBy, sortBy } from 'lodash-es';

import { useUserStore } from '@store/app.store';
import { useInitiatives } from '@services/query-services/initiatives.query-service';
import LabelsModal from '../modals/labels-modal.component';
import LabelsContainer from './labels-container.component';
import { useLabelsBar } from '@util/hooks/labels.hooks';
import AddButton from '@components/general/add-button.component';

const LabelsBar = ({
  meeting,
  card,
  modalDisclosure,
  children,
  showIconForNoLabels = false,
  ...props
}) => {
  const user = useUserStore((state) => state?.user);
  const isGuest = user?.isGuest;
  const disclosure = useDisclosure();
  modalDisclosure = modalDisclosure ?? disclosure;
  const labels = sortBy(meeting?.labels ?? card?.labels ?? [], 'title');

  const selectLabels = useCallback((allLabels) => keyBy(allLabels, 'id'), []);

  const { data: allLabels } = useInitiatives(
    {
      type: 'Label',
      has_archived_at: false
    },
    { select: selectLabels, enabled: !!(user && !isGuest) }
  );

  const enrichedLabels = useMemo(
    () =>
      labels.map((label) => ({
        ...label,
        ...allLabels?.[label?.id]
      })),
    [labels, allLabels]
  );

  const { containerRef, hiddenCount, isExpanded, setIsExpanded } = useLabelsBar(labels);

  // Calculate when Add Label button will be shown to conditionally adjust gap
  const hasLabels = labels?.length > 0;
  const shouldShowAddButton = showIconForNoLabels && !hasLabels;

  if (isGuest) {
    return null;
  }

  return (
    <Stack
      w='full'
      {...props}
    >
      <LabelsContainer
        ref={containerRef}
        labels={enrichedLabels}
        isExpanded={isExpanded}
      />
      <HStack gap={shouldShowAddButton ? 0 : { base: 0, md: 2 }}>
        {(isExpanded || hiddenCount > 0) && (
          <Button
            variant='reskinGhost'
            colorScheme='surface'
            size='xs'
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <Text variant='label-reg-md'>{isExpanded ? 'View less' : `+${hiddenCount} more`}</Text>
          </Button>
        )}

        <LabelHelperText
          shouldShowAddButton={shouldShowAddButton}
          hasLabels={hasLabels}
          modalDisclosure={modalDisclosure}
        />
        <Spacer />
        {children}
      </HStack>

      <LabelsModal
        meeting={meeting}
        card={card}
        disclosure={modalDisclosure}
      />
    </Stack>
  );
};

const LabelHelperText = ({ shouldShowAddButton, hasLabels, modalDisclosure }) => {
  if (shouldShowAddButton) {
    return (
      <AddButton
        text='Add Label'
        onClick={modalDisclosure?.onOpen}
        testId='add-label-card'
      />
    );
  } else {
    return (
      <Button
        variant='reskinGhost'
        colorScheme='surface'
        size='xs'
        onClick={modalDisclosure?.onOpen}
        data-testid={hasLabels ? 'edit-labels-button' : 'add-label-button'}
      >
        <Text variant='label-reg-md'>{hasLabels ? 'Edit labels' : 'Add a label'}</Text>
      </Button>
    );
  }
};

export default LabelsBar;
